import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Popover } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { fetchTagDefinitions } from "../../../api/tagDefinitions";
import { TWclassNames } from "../../utils/Div";
import { unreadEmailTagLabel } from "./unreadInboundMail";
import { CaseTagCombobox } from "./CaseTagCombobox";

/** Normalize API / JSON tag ids so number vs string mismatches do not break selection. */
export function coerceCaseTagId(id) {
  if (id === null || id === undefined || id === "") return null;
  if (typeof id === "number" && Number.isFinite(id)) return id;
  const n = Number(id);
  if (Number.isFinite(n) && String(n) === String(id).trim()) return n;
  return id;
}

export function normalizeCaseTagIds(ids) {
  const arr = Array.isArray(ids) ? ids : [];
  return arr.map(coerceCaseTagId).filter((id) => id !== null && id !== undefined && id !== "");
}

function tagIdsMatch(a, b) {
  return String(coerceCaseTagId(a)) === String(coerceCaseTagId(b));
}

/**
 * Multi-select for case-level tag_definitions (labels like "Henvendelse via Google").
 * Scope: tag_definitions.scope_module (e.g. case, task).
 *
 * @param {"popover"|"inline"|"combobox"} [variant] — `inline` avoids clipped dropdowns inside overflow panels (e.g. guide builder sidebar).
 *   `combobox` renders removable chips plus a searchable panel that can create new labels (case view).
 */
export function CaseTagSelector({
  value = [],
  onChange,
  readOnly,
  formik,
  name = "tag_ids",
  showBadges = true,
  scopeModule = "case",
  variant = "popover",
  definitions = null,
  buttonLabel = null,
  label = null,
  labelClassName = "",
  allowCreate = true,
}) {
  const { t } = useTranslation();
  const [tags, setTags] = useState(Array.isArray(definitions) ? definitions : []);
  const [loading, setLoading] = useState(!Array.isArray(definitions));

  const getSelectedRaw = () => (formik ? formik.values[name] || [] : value || []);

  const setSelectedIds = formik ? (ids) => formik.setFieldValue(name, ids) : onChange;

  // Latest selection for async callbacks (e.g. creating a label) that resolve after re-renders.
  const latestSelectedRef = useRef(null);
  latestSelectedRef.current = getSelectedRaw();

  useEffect(() => {
    if (Array.isArray(definitions)) {
      setTags(definitions);
      setLoading(false);
      return;
    }
    fetchTagDefinitions(scopeModule)
      .then((data) => setTags(Array.isArray(data) ? data : []))
      .catch(() => setTags([]))
      .finally(() => setLoading(false));
  }, [scopeModule, definitions]);

  const toggleTag = (tag) => {
    const tid = coerceCaseTagId(tag.id);
    if (tid === null || tid === undefined || tid === "") return;
    const prev = normalizeCaseTagIds(getSelectedRaw());
    const has = prev.some((id) => tagIdsMatch(id, tid));
    const next = has ? prev.filter((id) => !tagIdsMatch(id, tid)) : [...prev, tid];
    setSelectedIds(next);
  };

  const removeTag = (e, tag) => {
    e.stopPropagation();
    const tid = coerceCaseTagId(tag.id);
    const prev = normalizeCaseTagIds(getSelectedRaw());
    setSelectedIds(prev.filter((id) => !tagIdsMatch(id, tid)));
  };

  const selectedNorm = normalizeCaseTagIds(getSelectedRaw());
  const selectedTags = tags.filter((tag) => selectedNorm.some((id) => tagIdsMatch(id, tag.id)));

  if (readOnly) {
    return (
      <div className="flex flex-wrap gap-1">
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            className={TWclassNames(
              "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
              tag.color ? "" : "bg-gray-100 text-gray-800"
            )}
            style={tag.color ? { backgroundColor: tag.color + "30", color: tag.color } : {}}
          >
            {unreadEmailTagLabel(tag, t)}
          </span>
        ))}
      </div>
    );
  }

  const badges =
    showBadges && selectedTags.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            className={TWclassNames(
              "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
              tag.color ? "" : "bg-gray-100 text-gray-800"
            )}
            style={tag.color ? { backgroundColor: tag.color + "30", color: tag.color } : {}}
          >
            {unreadEmailTagLabel(tag, t)}
            <button
              type="button"
              onClick={(e) => removeTag(e, tag)}
              className="ml-0.5 hover:text-red-600 text-gray-500"
              aria-label={t("Remove")}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    ) : null;

  if (variant === "combobox") {
    return (
      <CaseTagCombobox
        tags={tags}
        loading={loading}
        selectedNorm={selectedNorm}
        tagIdsMatch={tagIdsMatch}
        toggleTag={toggleTag}
        clearAll={() => setSelectedIds([])}
        onTagCreated={(created) => {
          setTags((prev) => [...prev, created]);
          setSelectedIds([...normalizeCaseTagIds(latestSelectedRef.current), coerceCaseTagId(created.id)]);
        }}
        tagLabel={(tag) => unreadEmailTagLabel(tag, t)}
        label={label}
        labelClassName={labelClassName}
        scopeModule={scopeModule}
        allowCreate={allowCreate}
      />
    );
  }

  if (variant === "inline") {
    return (
      <div className="flex flex-col gap-2">
        <div className="max-h-52 overflow-y-auto rounded-md border border-gray-200 bg-white p-2">
          {loading ? (
            <div className="px-2 py-2 text-sm text-gray-500">{t("Loading...")}</div>
          ) : tags.length === 0 ? (
            <div className="px-2 py-2 text-sm text-gray-500">{t("No labels defined")}</div>
          ) : (
            <ul className="space-y-0.5">
              {tags.map((tag) => {
                const checked = selectedNorm.some((id) => tagIdsMatch(id, tag.id));
                return (
                  <li key={tag.id}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50">
                      <input type="checkbox" className="rounded border-gray-300 text-emerald-600" checked={checked} onChange={() => toggleTag(tag)} />
                      {tag.color ? (
                        <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
                      ) : null}
                      <span className="text-gray-800">{unreadEmailTagLabel(tag, t)}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {badges}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      <Popover className="relative">
        {({ open }) => (
          <TagPopoverBody
            open={open}
            loading={loading}
            tags={tags}
            selectedNorm={selectedNorm}
            toggleTag={toggleTag}
            buttonLabel={buttonLabel || t("Labels")}
            emptyLabel={t("No labels defined")}
            loadingLabel={t("Loading...")}
            tagLabel={(tag) => unreadEmailTagLabel(tag, t)}
            tagIdsMatch={tagIdsMatch}
          />
        )}
      </Popover>
      {badges}
    </div>
  );
}

function TagPopoverBody({
  open,
  loading,
  tags,
  selectedNorm,
  toggleTag,
  buttonLabel,
  emptyLabel,
  loadingLabel,
  tagIdsMatch,
  tagLabel,
}) {
  const buttonRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const width = 224;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setPos({ top: rect.bottom + 6, left });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  const panel = (
    <Popover.Panel
      static
      className="fixed z-[200] w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
      style={{ top: pos.top, left: pos.left }}
    >
      {loading ? (
        <div className="px-3 py-2 text-sm text-gray-500">{loadingLabel}</div>
      ) : tags.length === 0 ? (
        <div className="px-3 py-2 text-sm text-gray-500">{emptyLabel}</div>
      ) : (
        tags.map((tag) => (
          <button
            key={tag.id}
            type="button"
            onClick={() => toggleTag(tag)}
            className={TWclassNames(
              "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
              selectedNorm.some((id) => tagIdsMatch(id, tag.id))
                ? "bg-blue-50 text-blue-700"
                : "hover:bg-gray-50 text-gray-700"
            )}
          >
            {tag.color && (
              <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
            )}
            <span>{tagLabel ? tagLabel(tag) : tag.name}</span>
          </button>
        ))
      )}
    </Popover.Panel>
  );

  return (
    <>
      <Popover.Button
        ref={buttonRef}
        className="inline-flex items-center gap-x-1 rounded-full border border-dashed border-[#c3ccd8] px-2.5 py-1 text-xs font-semibold leading-6 text-[#6b7a8d] hover:border-[#8ea0b6] hover:text-[#33445a]"
      >
        <span>{buttonLabel}</span>
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </Popover.Button>
      {open && typeof document !== "undefined" ? createPortal(panel, document.body) : null}
    </>
  );
}
